import { CheckCircle2, XCircle, Lightbulb, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface ChallengeOutputProps {
  isLoading: boolean;
  result: {
    isCorrect: boolean;
    feedback: string;
    suggestions: string[];
  } | null;
}

export function ChallengeOutput({ isLoading, result }: ChallengeOutputProps) {
  if (isLoading) {
    return (
      <div className="rounded-lg border border-border bg-muted/30 p-6 flex items-center gap-3">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <span className="text-sm text-muted-foreground">Avaliando sua solução...</span>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="rounded-lg border border-border/50 bg-muted/20 p-6 text-center">
        <p className="text-sm text-muted-foreground">
          Escreva seu código e clique em "Verificar Solução" para receber feedback
        </p>
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg border p-5 space-y-4 ${
        result.isCorrect
          ? "border-emerald-500/30 bg-emerald-500/5"
          : "border-red-500/30 bg-red-500/5"
      }`}
    >
      {/* Status */}
      <div className="flex items-center gap-2">
        {result.isCorrect ? (
          <>
            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            <Badge className="bg-emerald-500/20 text-emerald-600 border-emerald-500/30 hover:bg-emerald-500/30">
              Correto!
            </Badge>
          </>
        ) : (
          <>
            <XCircle className="h-5 w-5 text-red-500" />
            <Badge className="bg-red-500/20 text-red-500 border-red-500/30 hover:bg-red-500/30">
              Tente novamente
            </Badge>
          </>
        )}
      </div>

      {/* Feedback */}
      <p className="text-sm text-foreground leading-relaxed">{result.feedback}</p>

      {/* Suggestions */}
      {result.suggestions?.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Lightbulb className="h-3.5 w-3.5" />
            Sugestões
          </div>
          <ul className="space-y-1">
            {result.suggestions.map((s, i) => (
              <li key={i} className="text-xs text-muted-foreground pl-4 relative before:content-['•'] before:absolute before:left-1 before:text-primary">
                {s}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
